import React, { useCallback, useId, useState } from "react";
import "./popup-with-api.scss";

export interface PopupWithApiItem {
  id: string;
  label: string;
  disabled?: boolean;
}

export const POPUP_WITH_API_DEFAULT_ITEMS: PopupWithApiItem[] = [
  { id: "cmm", label: "CMM" },
  { id: "text-note", label: "Text Note" },
  { id: "letter-upload", label: "Letter Upload" },
];

function CheckmarkIcon({ color }: { color?: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        d="M2 6L5 9L10 3"
        stroke={color ?? "currentColor"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface PopupWithApiProps {
  /** Checkbox rows; defaults to CMM, Text Note, Letter Upload from design. */
  items?: PopupWithApiItem[];
  /** Controlled: map of item id → checked. When set, `onCheckedChange` updates selection. */
  checkedById?: Record<string, boolean>;
  /** Uncontrolled initial selection. */
  defaultCheckedById?: Record<string, boolean>;
  onCheckedChange?: (next: Record<string, boolean>) => void;
  onCancel?: () => void;
  /**
   * Called when the user activates the right-hand action ("Mark as Success").
   * Use this to run your API request (e.g. `fetch`); may be async.
   */
  onMarkAsSuccess: () => void | Promise<void>;
  cancelLabel?: string;
  markSuccessLabel?: string;
  /** Shown on the primary button while `onMarkAsSuccess` is in flight */
  submittingLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Accessible name for the checkbox group */
  checkboxesGroupLabel?: string;
}

function buildInitialChecked(items: PopupWithApiItem[], seed?: Record<string, boolean>): Record<string, boolean> {
  const next: Record<string, boolean> = {};
  for (const item of items) {
    next[item.id] = seed?.[item.id] ?? false;
  }
  return next;
}

export function PopupWithApi(props: PopupWithApiProps) {
  const {
    items = POPUP_WITH_API_DEFAULT_ITEMS,
    checkedById: controlledChecked,
    defaultCheckedById,
    onCheckedChange,
    onCancel,
    onMarkAsSuccess,
    cancelLabel = "Cancel",
    markSuccessLabel = "Mark as Success",
    submittingLabel = "Saving…",
    className = "",
    style,
    checkboxesGroupLabel = "Options",
  } = props;

  const isControlled = controlledChecked != null;
  const [uncontrolledChecked, setUncontrolledChecked] = useState(() =>
    buildInitialChecked(items, defaultCheckedById)
  );
  const [submitting, setSubmitting] = useState(false);
  const baseId = useId();

  const checked = isControlled ? controlledChecked! : uncontrolledChecked;

  const setChecked = useCallback(
    (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => {
      if (isControlled) {
        const next = updater(controlledChecked!);
        onCheckedChange?.(next);
      } else {
        setUncontrolledChecked((prev) => {
          const next = updater(prev);
          onCheckedChange?.(next);
          return next;
        });
      }
    },
    [isControlled, controlledChecked, onCheckedChange]
  );

  const toggle = (id: string) => {
    const item = items.find((i) => i.id === id);
    if (item?.disabled) return;
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleMarkSuccess = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onMarkAsSuccess();
    } finally {
      setSubmitting(false);
    }
  };

  const rootClass = ["popup-with-api", className].filter(Boolean).join(" ");

  return (
    <div className={rootClass} style={style}>
      <div className="popup-with-api__list" role="group" aria-label={checkboxesGroupLabel}>
        {items.map((item) => {
          const isChecked = !!checked[item.id];
          const controlId = `${baseId}-cb-${item.id}`;
          return (
            <div key={item.id} className="popup-with-api__row">
              <button
                id={controlId}
                type="button"
                role="checkbox"
                aria-checked={isChecked}
                aria-disabled={item.disabled}
                disabled={item.disabled}
                className={`popup-with-api__checkbox${isChecked ? " popup-with-api__checkbox--checked" : ""}`}
                onClick={() => toggle(item.id)}
              >
                {isChecked && (
                  <span className="popup-with-api__check">
                    <CheckmarkIcon />
                  </span>
                )}
              </button>
              <label className="popup-with-api__label" htmlFor={controlId}>
                {item.label}
              </label>
            </div>
          );
        })}
      </div>
      <div className="popup-with-api__actions">
        <button type="button" className="popup-with-api__btn popup-with-api__btn--cancel" onClick={onCancel}>
          {cancelLabel}
        </button>
        <button
          type="button"
          className="popup-with-api__btn popup-with-api__btn--primary"
          onClick={handleMarkSuccess}
          disabled={submitting}
        >
          {submitting ? submittingLabel : markSuccessLabel}
        </button>
      </div>
    </div>
  );
}
